import { IcCapture } from '@assets/icons/24x24';
import { Box, HStack, Icon, Text } from '@chakra-ui/react';
import { useAppStore } from '@hooks/redux.hooks';
import { useWithDispatch } from '@hooks/useWithDispatch';
import { issueMapCommand } from '@redux/maps/mapsCommands.reducer';
import { selectSituationTime } from '@redux/situation/situation.selectors';
import { SHOW_PERIMETER_CAMERA_MIN_ZOOM } from '@utils/map/mapZoom.constants';
import { screenshotVideoAt1080pAndDownload } from '@utils/sensors/camera/screenshot.utils';
import { ReactNode, useCallback, useRef } from 'react';

import { Coordinates } from '@/types/commons/commons.types';
import { MapIdEnum } from '@/types/map.types';

interface OwnProps {
  name: string;
  position: Coordinates | null;
  children: ReactNode;
  showToolBar?: boolean;
}

export default function PerimeterCameraWrapper({ name, position, children, showToolBar = false }: Readonly<OwnProps>) {
  const store = useAppStore();
  const centerMapView = useWithDispatch(issueMapCommand);

  const ref = useRef<HTMLElement | undefined>(undefined);

  const downloadScreenshot = useCallback(() => {
    const situationTime = selectSituationTime(store.getState(), false);
    screenshotVideoAt1080pAndDownload(ref.current!, name, situationTime ?? 'UnknownDate');
  }, [name, store]);

  return (
    <Box ref={ref} position="relative" width="100%" height="100%">
      {children}
      {name && (
        <Text
          position="absolute"
          padding={1.5}
          top={2}
          left={2}
          backgroundColor="neutral.800-op70"
          cursor={position ? 'pointer' : 'default'}
          onClick={(e) => {
            position &&
              centerMapView({
                mapId: MapIdEnum.LIVE,
                command: {
                  type: 'CENTER',
                  payload: { center: position, zoom: SHOW_PERIMETER_CAMERA_MIN_ZOOM + 0.5 },
                },
              });
            e.stopPropagation();
          }}
        >
          {name}
        </Text>
      )}
      {showToolBar && (
        <HStack
          width="100%"
          position="absolute"
          gap={1.5}
          bottom={0}
          backgroundColor="900-op75"
          paddingY={1}
          paddingX={2}
        >
          <Icon
            as={IcCapture}
            boxSize="24px"
            marginLeft="auto"
            cursor="pointer"
            onClick={downloadScreenshot}
            _hover={{ color: 'skyBlue' }}
          />
        </HStack>
      )}
    </Box>
  );
}
